import { useFormik } from 'formik';
import { loginSchema } from '../forms/loginSchema';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import FormField from '../components/common/FormField';
import { showToast } from '../utils/showToast';
import { FcGoogle } from 'react-icons/fc';
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import authService from '../services/authService';

const LoginForm: React.FC = () => {
  const { loginUser, user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoginError('');
      try {
        await loginUser(values.email, values.password);
        showToast('✅ Login successful', 'success');
      } catch (err: any) {
        let errorMessage = '❌ Login failed. Try again.';
        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
        setLoginError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      if (user.isAdmin) {
        navigate('/dashboard/admin');
      } else if (user.role === 'mentor' && user.isApproved) {
        navigate('/dashboard/mentor');
      } else if (user.role === 'student' && user.isApproved) {
        navigate('/dashboard/student');
      } else if (user.role === 'student' && !user.isApproved) {
        showToast('⚠️ Your account is pending mentor approval.', 'info');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-5">
      {/* Email */}
      <FormField
        label="Email"
        name="email"
        type="email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
        placeholder="you@example.com"
      />

      {/* Password with toggle */}
      <div className="relative">
        <FormField
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && formik.errors.password ? formik.errors.password : ''}
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {/* Inline error message */}
      {loginError && (
        <p className="text-sm text-red-500 text-center">{loginError}</p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={formik.isSubmitting}
        className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
      >
        {formik.isSubmitting ? 'Logging in...' : 'Login'}
      </button>

      {/* Divider */}
      <div className="relative py-2 text-center text-sm text-gray-500">or</div>

      {/* Google Login */}
      <button
        type="button"
        onClick={authService.googleAuth}
        className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded hover:bg-gray-50 transition"
      >
        <FcGoogle className="text-xl" />
        <span className="text-sm font-medium text-gray-700">Sign in with Google</span>
      </button>
    </form>
  );
};

export default LoginForm;
